import apiRequest from "./ApiProvider";
import axios from "axios";
import { API_BASE_URL, AUTH_TOKEN } from "configs/AppConfig";

const PROJECT_API_BASE = `${API_BASE_URL}/commen`;

// ==================== WORKFLOW SERVICES ====================

/**
 * Create a new workflow
 */
export const createWorkflow = async (data) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/workflow/create`,
      data
    );
    return response;
  } catch (error) {
    console.error("Error creating workflow:", error);
    throw error;
  }
};

/**
 * Get all workflows
 */
export const getWorkflows = async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString
      ? `${PROJECT_API_BASE}/workflow/list?${queryString}`
      : `${PROJECT_API_BASE}/workflow/list`;
    const response = await apiRequest.get(url);
    return response;
  } catch (error) {
    console.error("Error fetching workflows:", error);
    throw error;
  }
};

/**
 * Get workflow by ID
 */
export const getWorkflowById = async (workflowId) => {
  try {
    const response = await apiRequest.get(
      `${PROJECT_API_BASE}/workflow/${workflowId}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching workflow:", error);
    throw error;
  }
};

/**
 * Update workflow
 */
export const updateWorkflow = async (workflowId, data) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/workflow/update/${workflowId}`,
      data
    );
    return response;
  } catch (error) {
    console.error("Error updating workflow:", error);
    throw error;
  }
};

/**
 * Delete workflow
 */
export const deleteWorkflow = async (workflowId) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/workflow/delete/${workflowId}`
    );
    return response;
  } catch (error) {
    console.error("Error deleting workflow:", error);
    throw error;
  }
};

// ==================== PROJECT SERVICES ====================

/**
 * Create a new project
 */
export const createProject = async (data) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/project/create`,
      data
    );
    return response;
  } catch (error) {
    console.error("Error creating project:", error);
    throw error;
  }
};

/**
 * Get all projects
 */
export const getProjects = async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString
      ? `${PROJECT_API_BASE}/project/list?${queryString}`
      : `${PROJECT_API_BASE}/project/list`;
    const response = await apiRequest.get(url);
    return response;
  } catch (error) {
    console.error("Error fetching projects:", error);
    throw error;
  }
};

/**
 * Get project by ID
 */
export const getProjectById = async (projectId) => {
  try {
    const response = await apiRequest.get(
      `${PROJECT_API_BASE}/project/${projectId}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching project:", error);
    throw error;
  }
};

/**
 * Update project
 */
export const updateProject = async (projectId, data) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/project/update/${projectId}`,
      data
    );
    return response;
  } catch (error) {
    console.error("Error updating project:", error);
    throw error;
  }
};

/**
 * Delete project
 */
export const deleteProject = async (projectId) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/project/delete/${projectId}`
    );
    return response;
  } catch (error) {
    console.error("Error deleting project:", error);
    throw error;
  }
};

// ==================== TASK CATEGORY SERVICES ====================

/**
 * Create a new task category
 */
export const createTaskCategory = async (data) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/task-category/create`,
      data
    );
    return response;
  } catch (error) {
    console.error("Error creating task category:", error);
    throw error;
  }
};

/**
 * Get all task categories for a project
 */
export const getTaskCategories = async (projectId) => {
  try {
    const response = await apiRequest.get(
      `${PROJECT_API_BASE}/task-category/list/${projectId}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching task categories:", error);
    throw error;
  }
};

/**
 * Get task category by ID
 */
export const getTaskCategoryById = async (categoryId) => {
  try {
    const response = await apiRequest.get(
      `${PROJECT_API_BASE}/task-category/${categoryId}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching task category:", error);
    throw error;
  }
};

/**
 * Update task category
 */
export const updateTaskCategory = async (categoryId, data) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/task-category/update/${categoryId}`,
      data
    );
    return response;
  } catch (error) {
    console.error("Error updating task category:", error);
    throw error;
  }
};

/**
 * Delete task category
 */
export const deleteTaskCategory = async (categoryId) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/task-category/delete/${categoryId}`
    );
    return response;
  } catch (error) {
    console.error("Error deleting task category:", error);
    throw error;
  }
};

// ==================== TASK SERVICES ====================

/**
 * Create a new task
 */
export const createTask = async (data) => {
  try {
    // Check if there are files to upload
    const hasFiles = data.files && Array.isArray(data.files) && data.files.length > 0 && data.files.some(file => file instanceof File);
    
    if (hasFiles) {
      // Use FormData for file uploads
      const formData = new FormData();
      
      // Add text fields
      if (data.title) formData.append("title", data.title);
      if (data.description !== undefined) formData.append("description", data.description || "");
      if (data.taskCategoryId) formData.append("taskCategoryId", data.taskCategoryId);
      if (data.projectId) formData.append("projectId", data.projectId);
      if (data.priority) formData.append("priority", data.priority);
      if (data.workflowStageId) formData.append("workflowStageId", data.workflowStageId);
      if (data.startDate) formData.append("startDate", data.startDate);
      if (data.dueDate) formData.append("dueDate", data.dueDate);
      
      // Add assigned users
      if (data.assignedUsers && Array.isArray(data.assignedUsers)) {
        data.assignedUsers.forEach(userId => {
          formData.append("assignedUsers", userId);
        });
      }
      
      // Add files
      data.files.forEach((file) => {
        if (file instanceof File) {
          formData.append("files", file);
        }
      });
      
      // Use axios directly for FormData
      const response = await axios.post(
        `${PROJECT_API_BASE}/task/create`,
        formData,
        {
          headers: {
            Authorization: "Bearer " + (localStorage.getItem(AUTH_TOKEN) || ""),
          },
        }
      );
      
      return response.data;
    } else {
      // Regular JSON request without files
      const response = await apiRequest.post(
        `${PROJECT_API_BASE}/task/create`,
        data
      );
      return response;
    }
  } catch (error) {
    console.error("Error creating task:", error);
    throw error;
  }
};

/**
 * Get tasks with filters
 */
export const getTasks = async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString
      ? `${PROJECT_API_BASE}/task/list?${queryString}`
      : `${PROJECT_API_BASE}/task/list`;
    const response = await apiRequest.get(url);
    return response;
  } catch (error) {
    console.error("Error fetching tasks:", error);
    throw error;
  }
};

/**
 * Get task by ID
 */
export const getTaskById = async (taskId) => {
  try {
    const response = await apiRequest.get(
      `${PROJECT_API_BASE}/task/${taskId}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching task:", error);
    throw error;
  }
};

/**
 * Update task
 */
export const updateTask = async (taskId, data) => {
  try {
    // Check if there are new files to upload or existing files to keep
    const hasNewFiles = data.files && Array.isArray(data.files) && data.files.some(file => file instanceof File);
    const hasExistingFiles = data.existingFiles && Array.isArray(data.existingFiles) && data.existingFiles.length > 0;
    
    if (hasNewFiles || hasExistingFiles) {
      // Use FormData for file uploads
      const formData = new FormData();
      
      // Add text fields
      if (data.title !== undefined) formData.append("title", data.title);
      if (data.description !== undefined) formData.append("description", data.description || "");
      if (data.taskCategoryId) formData.append("taskCategoryId", data.taskCategoryId);
      if (data.projectId) formData.append("projectId", data.projectId);
      if (data.priority) formData.append("priority", data.priority);
      if (data.workflowStageId) formData.append("workflowStageId", data.workflowStageId);
      if (data.startDate !== undefined) formData.append("startDate", data.startDate || "");
      if (data.dueDate !== undefined) formData.append("dueDate", data.dueDate || "");
      
      // Add assigned users
      if (data.assignedUsers && Array.isArray(data.assignedUsers)) {
        data.assignedUsers.forEach(userId => {
          formData.append("assignedUsers", userId);
        });
      }
      
      // Add existing files (keep them) as separate field
      if (hasExistingFiles) {
        data.existingFiles.forEach((file) => {
          if (typeof file === 'string') {
            formData.append("existingFiles", file);
          }
        });
      }
      
      // Add new files
      if (hasNewFiles) {
        data.files.forEach((file) => {
          if (file instanceof File) {
            formData.append("files", file);
          }
        });
      }
      
      // Use axios directly for FormData
      const response = await axios.post(
        `${PROJECT_API_BASE}/task/update/${taskId}`,
        formData,
        {
          headers: {
            Authorization: "Bearer " + (localStorage.getItem(AUTH_TOKEN) || ""),
          },
        }
      );
      
      return response.data;
    } else {
      // Regular JSON request without files
      const response = await apiRequest.post(
        `${PROJECT_API_BASE}/task/update/${taskId}`,
        data
      );
      return response;
    }
  } catch (error) {
    console.error("Error updating task:", error);
    throw error;
  }
};

/**
 * Update task order (for drag and drop)
 */
export const updateTaskOrder = async (taskId, order, workflowStageId = null) => {
  try {
    const payload = { order };
    if (workflowStageId) {
      payload.workflowStageId = workflowStageId;
    }
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/task/update-order/${taskId}`,
      payload
    );
    return response;
  } catch (error) {
    console.error("Error updating task order:", error);
    throw error;
  }
};

/**
 * Delete task
 */
export const deleteTask = async (taskId) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/task/delete/${taskId}`
    );
    return response;
  } catch (error) {
    console.error("Error deleting task:", error);
    throw error;
  }
};

/**
 * Get tasks for Kanban board
 */
export const getTasksForKanban = async (projectId, taskCategoryId = null) => {
  try {
    const params = taskCategoryId ? { taskCategoryId } : {};
    const queryString = new URLSearchParams(params).toString();
    const url = queryString
      ? `${PROJECT_API_BASE}/task/kanban/${projectId}?${queryString}`
      : `${PROJECT_API_BASE}/task/kanban/${projectId}`;
    const response = await apiRequest.get(url);
    return response;
  } catch (error) {
    console.error("Error fetching Kanban data:", error);
    throw error;
  }
};

// ==================== TASK COMMENT SERVICES ====================

/**
 * Create a new task comment
 */
export const createTaskComment = async (data) => {
  try {
    const formData = new FormData();
    
    // Add text fields
    formData.append("taskId", data.taskId);
    formData.append("projectId", data.projectId);
    if (data.message) {
      formData.append("message", data.message);
    }
    
    // Add images
    if (data.images && Array.isArray(data.images)) {
      data.images.forEach((image) => {
        if (image instanceof File) {
          formData.append("images", image);
        }
      });
    }
    
    // Add files
    if (data.files && Array.isArray(data.files)) {
      data.files.forEach((file) => {
        if (file instanceof File) {
          formData.append("files", file);
        }
      });
    }
    
    // Use axios directly for FormData to ensure proper headers
    const response = await axios.post(
      `${API_BASE_URL}/commen/task-comment/create`,
      formData,
      {
        headers: {
          Authorization: "Bearer " + (localStorage.getItem(AUTH_TOKEN) || ""),
          // Don't set Content-Type - let axios set it automatically with boundary
        },
      }
    );
    
    return response.data;
  } catch (error) {
    console.error("Error creating task comment:", error);
    throw error;
  }
};

/**
 * Get all comments for a task
 */
export const getTaskComments = async (taskId) => {
  try {
    const response = await apiRequest.get(
      `${PROJECT_API_BASE}/task-comment/list/${taskId}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching task comments:", error);
    throw error;
  }
};

/**
 * Get comment by ID
 */
export const getTaskCommentById = async (commentId) => {
  try {
    const response = await apiRequest.get(
      `${PROJECT_API_BASE}/task-comment/${commentId}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching task comment:", error);
    throw error;
  }
};

/**
 * Update task comment
 */
export const updateTaskComment = async (commentId, data) => {
  try {
    const formData = new FormData();
    
    // Add text fields
    if (data.message !== undefined) {
      formData.append("message", data.message || "");
    }
    
    // Add existing images (as strings) - preserve them
    if (data.existingImages && Array.isArray(data.existingImages)) {
      data.existingImages.forEach((image) => {
        if (typeof image === "string") {
          formData.append("existingImages", image);
        }
      });
    }
    
    // Add existing files (as strings) - preserve them
    if (data.existingFiles && Array.isArray(data.existingFiles)) {
      data.existingFiles.forEach((file) => {
        if (typeof file === "string") {
          formData.append("existingFiles", file);
        }
      });
    }
    
    // Add new images (File objects)
    if (data.images && Array.isArray(data.images)) {
      data.images.forEach((image) => {
        if (image instanceof File) {
          formData.append("images", image);
        }
      });
    }
    
    // Add new files (File objects)
    if (data.files && Array.isArray(data.files)) {
      data.files.forEach((file) => {
        if (file instanceof File) {
          formData.append("files", file);
        }
      });
    }
    
    // Use axios directly for FormData
    const response = await axios.post(
      `${API_BASE_URL}/commen/task-comment/update/${commentId}`,
      formData,
      {
        headers: {
          Authorization: "Bearer " + (localStorage.getItem(AUTH_TOKEN) || ""),
        },
      }
    );
    
    return response.data;
  } catch (error) {
    console.error("Error updating task comment:", error);
    throw error;
  }
};

/**
 * Delete task comment
 */
export const deleteTaskComment = async (commentId) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/task-comment/delete/${commentId}`
    );
    return response;
  } catch (error) {
    console.error("Error deleting task comment:", error);
    throw error;
  }
};

// ==================== TASK TIME TRACKING SERVICES ====================

/**
 * Start timer for a task
 */
export const startTimer = async (taskId) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/task-timer/start/${taskId}`
    );
    return response;
  } catch (error) {
    console.error("Error starting timer:", error);
    throw error;
  }
};

/**
 * Stop timer
 */
export const stopTimer = async (timeLogId) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/task-timer/stop/${timeLogId}`
    );
    return response;
  } catch (error) {
    console.error("Error stopping timer:", error);
    throw error;
  }
};

/**
 * Get active timer for current user
 */
export const getActiveTimer = async () => {
  try {
    const response = await apiRequest.get(
      `${PROJECT_API_BASE}/task-timer/active`
    );
    return response;
  } catch (error) {
    console.error("Error fetching active timer:", error);
    throw error;
  }
};

/**
 * Get all time logs for a task
 */
export const getTaskTimeLogs = async (taskId) => {
  try {
    const response = await apiRequest.get(
      `${PROJECT_API_BASE}/task-timer/logs/${taskId}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching task time logs:", error);
    throw error;
  }
};

/**
 * Get all time logs for current user
 */
export const getUserTimeLogs = async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString
      ? `${PROJECT_API_BASE}/task-timer/user-logs?${queryString}`
      : `${PROJECT_API_BASE}/task-timer/user-logs`;
    const response = await apiRequest.get(url);
    return response;
  } catch (error) {
    console.error("Error fetching user time logs:", error);
    throw error;
  }
};

/**
 * Create manual time log entry
 */
export const createManualTimeLog = async (data) => {
  try {
    const formData = new FormData();
    
    // Add text fields
    if (data.taskId) formData.append("taskId", data.taskId);
    if (data.projectId) formData.append("projectId", data.projectId);
    if (data.startTime) formData.append("startTime", data.startTime);
    if (data.endTime) formData.append("endTime", data.endTime);
    if (data.durationSeconds !== undefined) formData.append("durationSeconds", data.durationSeconds);
    if (data.durationMinutes !== undefined) formData.append("durationMinutes", data.durationMinutes);
    if (data.description !== undefined) formData.append("description", data.description || "");
    if (data.isManual !== undefined) formData.append("isManual", data.isManual);
    if (data.logType) formData.append("logType", data.logType);
    
    // Add files
    if (data.files && Array.isArray(data.files)) {
      data.files.forEach((file) => {
        if (file instanceof File || (file && file.originFileObj)) {
          formData.append("files", file instanceof File ? file : file.originFileObj);
        }
      });
    }
    
    // Use axios directly for FormData
    const response = await axios.post(
      `${API_BASE_URL}/commen/task-timer/manual`,
      formData,
      {
        headers: {
          Authorization: "Bearer " + (localStorage.getItem(AUTH_TOKEN) || ""),
        },
      }
    );
    
    return response.data;
  } catch (error) {
    console.error("Error creating manual time log:", error);
    throw error;
  }
};

/**
 * Update time log entry
 */
export const updateTimeLog = async (timeLogId, data) => {
  try {
    const formData = new FormData();
    
    // Add text fields
    if (data.taskId) formData.append("taskId", data.taskId);
    if (data.projectId) formData.append("projectId", data.projectId);
    if (data.startTime) formData.append("startTime", data.startTime);
    if (data.endTime) formData.append("endTime", data.endTime);
    if (data.durationSeconds !== undefined) formData.append("durationSeconds", data.durationSeconds);
    if (data.durationMinutes !== undefined) formData.append("durationMinutes", data.durationMinutes);
    if (data.description !== undefined) formData.append("description", data.description || "");
    if (data.isManual !== undefined) formData.append("isManual", data.isManual);
    
    // Add files (for update, we append new files to existing ones)
    if (data.files && Array.isArray(data.files)) {
      data.files.forEach((file) => {
        if (file instanceof File || (file && file.originFileObj)) {
          formData.append("files", file instanceof File ? file : file.originFileObj);
        } else if (typeof file === "string") {
          // Keep existing files
          formData.append("existingFiles", file);
        }
      });
    }
    
    // Use axios directly for FormData
    const response = await axios.post(
      `${API_BASE_URL}/commen/task-timer/update/${timeLogId}`,
      formData,
      {
        headers: {
          Authorization: "Bearer " + (localStorage.getItem(AUTH_TOKEN) || ""),
        },
      }
    );
    
    return response.data;
  } catch (error) {
    console.error("Error updating time log:", error);
    throw error;
  }
};

/**
 * Delete time log entry
 */
export const deleteTimeLog = async (timeLogId) => {
  try {
    const response = await apiRequest.post(
      `${PROJECT_API_BASE}/task-timer/delete/${timeLogId}`
    );
    return response;
  } catch (error) {
    console.error("Error deleting time log:", error);
    throw error;
  }
};

